import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Users, Share2, TrendingUp, CheckCircle, BookOpen, Clock, AlertCircle, BarChart3 } from 'lucide-react';
import { TeamLeadService } from '../../services/teamLeadService';

const TeamLeadDashboard = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [showApprovalRequests, setShowApprovalRequests] = useState(false);

  const { data: statsData, isLoading } = useQuery({
    queryKey: ['teamLeadStats', user?.id],
    queryFn: async () => {
      const result = await TeamLeadService.getTeamStats(user?.id);
      console.log('Team Lead Stats Response:', result);
      return result;
    },
    enabled: !!user?.id,
  });

  const { data: teamMembersData } = useQuery({
    queryKey: ['teamLeadMembers', user?.id],
    queryFn: async () => {
      const result = await TeamLeadService.getTeamMembers(user?.id);
      console.log('Team Members Response:', result);
      return result;
    },
    enabled: !!user?.id,
  });

  const { data: approvalRequestsData } = useQuery({
    queryKey: ['teamLeadApprovalRequests', user?.id],
    queryFn: () => TeamLeadService.getApprovalRequests(user?.id),
    enabled: !!user?.id && showApprovalRequests,
  });

  // Handle different response formats
  const stats = statsData?.success ? (statsData?.data || {}) : (statsData || {});
  const teamMembers = teamMembersData?.success ? (teamMembersData?.data || []) : (teamMembersData || []);
  const approvalRequests = approvalRequestsData?.data || [];
  
  // Debug: Log the raw data
  if (statsData) {
    console.log('Raw statsData:', statsData);
    console.log('Extracted stats:', stats);
  }
  if (teamMembersData) {
    console.log('Raw teamMembersData:', teamMembersData);
    console.log('Extracted teamMembers:', teamMembers);
  }
  
  // Normalize the stats object to handle different field names
  const normalizedStats = {
    teamSize: stats.teamSize || stats.team_size || stats.teamMembers || teamMembers?.length || 0,
    totalLeads: stats.totalLeads || stats.total_leads || stats.total || 0,
    distributed: stats.distributed || stats.distributed_leads || 0,
    confirmed: stats.confirmed || stats.confirmed_leads || stats.converted || 0,
    booked: stats.booked || stats.booked_leads || stats.booked_orders || 0,
    conversionRate: stats.conversionRate || stats.conversion_rate || 
      (stats.totalLeads || stats.total_leads ? 
        Math.round(((stats.confirmed || stats.confirmed_leads || 0) / (stats.totalLeads || stats.total_leads)) * 100) : 0),
  };
  
  console.log('Normalized Stats:', normalizedStats);

  const handleApprovalAction = async (requestId, status) => {
    const result = await TeamLeadService.updateApprovalRequest(requestId, status, '');
    if (result.success) {
      alert(`Request ${status === 'approved' ? 'approved' : 'rejected'} successfully!`);
      window.location.reload();
    } else {
      alert('Failed to update request: ' + result.error);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
          <h1 className="text-3xl font-bold mb-2">Team Lead Dashboard</h1>
          <p className="text-white/80">Manage your team and oversee sales operations</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
        <h1 className="text-3xl font-bold mb-2">Team Lead Dashboard</h1>
        <p className="text-white/80">Manage your team and oversee sales operations</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Team Size</p>
              <p className="text-2xl font-bold text-gray-900">{normalizedStats.teamSize}</p>
            </div>
            <Users size={24} className="text-indigo-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Leads</p>
              <p className="text-2xl font-bold text-gray-900">{normalizedStats.totalLeads}</p>
            </div>
            <Share2 size={24} className="text-purple-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Confirmed</p>
              <p className="text-2xl font-bold text-gray-900">{normalizedStats.confirmed}</p>
            </div>
            <CheckCircle size={24} className="text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Booked</p>
              <p className="text-2xl font-bold text-gray-900">{normalizedStats.booked}</p>
            </div>
            <BookOpen size={24} className="text-blue-600" />
          </div>
        </div>
        {normalizedStats.conversionRate > 0 && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Conversion Rate</p>
                <p className="text-2xl font-bold text-gray-900">{normalizedStats.conversionRate}%</p>
              </div>
              <TrendingUp size={24} className="text-indigo-600" />
            </div>
          </div>
        )}
        {normalizedStats.distributed > 0 && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Distributed</p>
                <p className="text-2xl font-bold text-gray-900">{normalizedStats.distributed}</p>
              </div>
              <Share2 size={24} className="text-purple-600" />
            </div>
          </div>
        )}
      </div>

      {/* Team Members Quick View */}
      {teamMembers.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Team Members</h2>
            <button
              onClick={() => navigate('/teamlead/team-management')}
              className="text-sm text-indigo-600 hover:text-indigo-800"
            >
              View All
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teamMembers.slice(0, 6).map((member) => (
              <div key={member.id} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                    <span className="text-indigo-600 font-semibold">
                      {member.name?.charAt(0).toUpperCase() || 'T'}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{member.name || 'Unknown'}</p>
                    <p className="text-sm text-gray-600">{member.email || 'N/A'}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Quick Actions</h2>
          <button
            onClick={() => setShowApprovalRequests(!showApprovalRequests)}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center space-x-2"
          >
            <AlertCircle size={16} />
            <span>{showApprovalRequests ? 'Hide' : 'Show'} Approval Requests</span>
            {approvalRequests.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                {approvalRequests.length}
              </span>
            )}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button 
            onClick={() => navigate('/teamlead/lead-distribution')}
            className="px-4 py-3 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600"
          >
            Distribute Leads
          </button>
          <button 
            onClick={() => navigate('/teamlead/performance')}
            className="px-4 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
          >
            View Performance
          </button>
          <button 
            onClick={() => navigate('/teamlead/booked')}
            className="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            View Booked Orders
          </button>
          <button 
            onClick={() => navigate('/teamlead/lead-confirmation')}
            className="px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            Lead Confirmation
          </button>
          <button 
            onClick={() => navigate('/teamlead/courier-booking')}
            className="px-4 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
          >
            Courier Booking
          </button>
          <button 
            onClick={() => navigate('/teamlead/attendance')}
            className="px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            Attendance Management
          </button>
        </div>
      </div>

      {/* Approval Requests */}
      {showApprovalRequests && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Pending Approval Requests</h2>
          {approvalRequests.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No pending approval requests</p>
          ) : (
            <div className="space-y-3">
              {approvalRequests.slice(0, 5).map((request) => (
                <div key={request.id} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">Lead ID: {request.leadId?.slice(0, 8)}</p>
                      <p className="text-sm text-gray-600">Telecaller: {request.telecallerId?.slice(0, 8)}</p>
                      {request.notes && <p className="text-sm text-gray-500 mt-1">{request.notes}</p>}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleApprovalAction(request.id, 'approved')}
                        className="px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleApprovalAction(request.id, 'rejected')}
                        className="px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamLeadDashboard;

